import { DAY, HOUR, toInstant, toLocal, dateOf, addDays, startOfDay, type Instant, type LocalDate, type LocalDateTime } from './time';
import { span } from './pricing';
import type { Block, BookingRecord, Phase, World } from './types';

/**
 * What the UI shows. Evaluated against the real clock on every read, so an expired hold stops
 * blocking dates even if no device was open at the moment it expired.
 */
export function phase(r: Pick<BookingRecord, 'status' | 'hold' | 'items'>, now: Instant): Phase {
  if (r.status === 'hold') return !r.hold || toInstant(r.hold.expires) <= now ? 'expired' : 'hold';
  if (r.status === 'confirmed') {
    const { start, end } = span(r);
    return now < toInstant(start) ? 'upcoming' : now < toInstant(end) ? 'inprogress' : 'closing';
  }
  return r.status;
}

const BLOCKING: ReadonlySet<Phase> = new Set(['hold', 'upcoming', 'inprogress', 'closing']);
export const isBlocking = (r: BookingRecord, now: Instant) => BLOCKING.has(phase(r, now));

export interface Conflict {
  id: string;
  kind: 'record' | 'block';
  phase: Phase | 'block';
  start: LocalDateTime;
  end: LocalDateTime;
  type: string;
}

/**
 * Interval semantics (all half-open, [start, end)):
 *  - Two holds/bookings clash when  a.start < b.end + buffer  and  a.end + buffer > b.start.
 *    So with a 2 h buffer, a 12:00 check-out and a 14:00 event start do NOT clash; 13:59 does.
 *  - Owner blocks clash on plain overlap (no buffer): the owner sets their exact times.
 *  - Enquiries, expired holds, lost, cancelled and completed records never block.
 */
export function conflicts(world: World, start: LocalDateTime, end: LocalDateTime, opts: { now: Instant; bufferHours: number; excludeId?: string | null }): Conflict[] {
  const s = toInstant(start), e = toInstant(end), buf = opts.bufferHours * HOUR, out: Conflict[] = [];
  for (const r of world.records) {
    if (r.id === opts.excludeId || !isBlocking(r, opts.now)) continue;
    const sp = span(r), rs = toInstant(sp.start), re = toInstant(sp.end);
    if (s < re + buf && e + buf > rs) out.push({ id: r.id, kind: 'record', phase: phase(r, opts.now), start: sp.start, end: sp.end, type: r.type });
  }
  for (const b of world.blocks) {
    if (b.id === opts.excludeId) continue;
    if (s < toInstant(b.end) && e > toInstant(b.start)) out.push({ id: b.id, kind: 'block', phase: 'block', start: b.start, end: b.end, type: b.type });
  }
  return out.sort((a, b) => toInstant(a.start) - toInstant(b.start));
}

/** Open enquiries for overlapping dates ("competing" — first to hold or confirm wins). */
export function competing(world: World, start: LocalDateTime, end: LocalDateTime, excludeId?: string | null): BookingRecord[] {
  const s = toInstant(start), e = toInstant(end);
  return world.records.filter(r => r.id !== excludeId && r.status === 'enquiry').filter(r => {
    const sp = span(r);
    return s < toInstant(sp.end) && e > toInstant(sp.start);
  });
}

export const ALT_OFFSETS = [7, 1, -1, 2, -2, 14, 3, -3, -7, 4, -4, 5, -5, 6, -6, 8, 9, 10, 13, 15, 21] as const;

export interface Alternative { start: LocalDateTime; end: LocalDateTime; offset: number; sameWeekday: boolean }

/** First n conflict-free shifts of the same duration, skipping any that start in the past. */
export function alternatives(world: World, start: LocalDateTime, end: LocalDateTime, opts: { now: Instant; bufferHours: number; excludeId?: string | null; n?: number }): Alternative[] {
  const out: Alternative[] = [], s0 = toInstant(start), e0 = toInstant(end);
  for (const o of ALT_OFFSETS) {
    // IST has no DST, so shifting by whole days keeps the wall-clock time.
    const s = toLocal(s0 + o * DAY), e = toLocal(e0 + o * DAY);
    if (toInstant(s) < opts.now) continue;
    if (!conflicts(world, s, e, opts).length) out.push({ start: s, end: e, offset: o, sameWeekday: o % 7 === 0 });
    if (out.length >= (opts.n ?? 3)) break;
  }
  return out;
}

export type DayState = 'free' | 'partial' | 'full';
export interface DayInfo { state: DayState; from?: LocalDateTime; until?: LocalDateTime }

export const DAY_OPEN_HOUR = 7;
export const DAY_CLOSE_HOUR = 23;
export const MIN_FREE_WINDOW_HOURS = 4;

/**
 * free: nothing blocking touches the day.
 * partial: a free window of at least 4 h exists between 07:00 and 23:00 (the first such window is reported).
 * full: otherwise.
 * Blocking records count with their turnover buffer; owner blocks without.
 */
export function dayInfo(world: World, date: LocalDate, opts: { now: Instant; bufferHours: number }): DayInfo {
  const d0 = startOfDay(date), d1 = d0 + DAY, buf = opts.bufferHours * HOUR, iv: [number, number][] = [];
  for (const r of world.records) {
    if (!isBlocking(r, opts.now)) continue;
    const sp = span(r), s = toInstant(sp.start) - buf, e = toInstant(sp.end) + buf;
    if (s < d1 && e > d0) iv.push([Math.max(s, d0), Math.min(e, d1)]);
  }
  for (const b of world.blocks) {
    const s = toInstant(b.start), e = toInstant(b.end);
    if (s < d1 && e > d0) iv.push([Math.max(s, d0), Math.min(e, d1)]);
  }
  if (!iv.length) return { state: 'free' };
  iv.sort((a, b) => a[0] - b[0]);
  const open = d0 + DAY_OPEN_HOUR * HOUR, close = d0 + DAY_CLOSE_HOUR * HOUR, min = MIN_FREE_WINDOW_HOURS * HOUR;
  let cur = open;
  for (const [s, e] of iv) {
    if (Math.min(s, close) - cur >= min) return { state: 'partial', from: toLocal(cur), until: toLocal(Math.min(s, close)) };
    cur = Math.max(cur, e);
  }
  if (close - cur >= min) return { state: 'partial', from: toLocal(cur), until: toLocal(close) };
  return { state: 'full' };
}

/** "Free until 12 PM" when the window starts at opening time, else "Free from 2 PM". */
export function dayInfoText(info: DayInfo, fTime: (s: LocalDateTime) => string): string {
  if (info.state === 'free') return 'Available';
  if (info.state === 'full') return 'Booked';
  const opensAtStart = info.from!.slice(11) === `${String(DAY_OPEN_HOUR).padStart(2, '0')}:00`;
  return opensAtStart ? `Free until ${fTime(info.until!)}` : `Free from ${fTime(info.from!)}`;
}

export interface Verdict { state: 'available' | 'partly' | 'unavailable' | 'past'; conflicts: Conflict[] }

/** Calendar range verdict over whole days. */
export function rangeVerdict(world: World, from: LocalDate, to: LocalDate, opts: { now: Instant; bufferHours: number }): Verdict & { days: { date: LocalDate; info: DayInfo }[] } {
  const days: { date: LocalDate; info: DayInfo }[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) days.push({ date: d, info: dayInfo(world, d, opts) });
  const cs = conflicts(world, `${from}T00:00`, `${addDays(to, 1)}T00:00`, opts);
  const state = from < dateOf(opts.now) ? 'past'
    : days.every(x => x.info.state === 'free') ? 'available'
    : days.some(x => x.info.state === 'full') ? 'unavailable' : 'partly';
  return { state, conflicts: cs, days };
}

/**
 * Pairs of blocking records (or record + block) that clash with each other. Normally impossible
 * because holds and confirmations check first, but two devices can both pass that check before
 * either sees the other's change. Run this after every sync; the owner must resolve each pair.
 */
export function overlapPairs(world: World, opts: { now: Instant; bufferHours: number }): { a: string; b: string; kind: 'record' | 'block' }[] {
  const out: { a: string; b: string; kind: 'record' | 'block' }[] = [];
  const blocking = world.records.filter(r => isBlocking(r, opts.now));
  for (let i = 0; i < blocking.length; i++) {
    const r = blocking[i]!, sp = span(r);
    for (const c of conflicts({ records: blocking.slice(i + 1), blocks: world.blocks }, sp.start, sp.end, opts)) out.push({ a: r.id, b: c.id, kind: c.kind });
  }
  return out;
}

export function blockOverlapsBlock(a: Block, b: Block): boolean {
  return toInstant(a.start) < toInstant(b.end) && toInstant(a.end) > toInstant(b.start);
}
